import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Flex, Text
} from '@chakra-ui/react';
import useApi from 'hooks/useApi';
import Pagination from 'components/pagination/Pagination';
import ErrorDisplay from 'components/errorDisplay/ErrorDisplay';
import { showToast } from 'components/toast/Toast';
import { OverlayLoaderComponent } from 'components/loader/Loader';
import SidebarFilter from 'components/sidebarFilter/SidebarFilter';
import { ENTITY_FILTER_TYPES } from 'utils/common';
import { getFilterFromLocalStorage } from 'utils/common';
import { setFilterToLocalStorage } from 'utils/common';
import NoDataFound from 'components/noDataFound/NoDataFound';
import { ENTITY_COLUMNS_TYPES } from 'utils/common';
import CustomTable from 'components/table/CustomTable';
import { USER_STATUS } from 'constants/filterDropdownData';
import Menu from 'components/menu/MainMenu';
import { viewNumberTableColumn } from './variables';
import { useHistory } from 'react-router-dom';
import { VIEW_NUMBER_USAGE } from 'constants/endpoints';
import { InputGroup } from '@chakra-ui/react/dist/chakra-ui-react.cjs';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';


const PAGE_SIZE = 50;
const today = new Date();
const tenDaysAgo = new Date();
tenDaysAgo.setDate(today.getDate() - 10);

export default function ViewNumberUsage() {
  const { apiCall, loading } = useApi();
  const [viewNumberData, setViewNumberData] = useState([]);
  const [apiError, setApiError] = useState(null);
  const lsFiltersPageSize = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.REPORT_PAGE_SIZE
  );
  const [pageSize, setPageSize] = useState(
    lsFiltersPageSize && Number.isInteger(lsFiltersPageSize)
      ? lsFiltersPageSize
      : PAGE_SIZE
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { search } = useLocation();
  const history = useHistory();
  const queryObject = Object.fromEntries(new URLSearchParams(search));
  const [selectedAction, setSelectedAction] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedRowsData, setSelectedRowsData] = useState([]);

  const fetchData = useCallback(
    async (text) => {
      const validPageSize = Number.isInteger(pageSize) ? pageSize : PAGE_SIZE;
      try {
        const response = await apiCall.get(`${VIEW_NUMBER_USAGE}`, {
          page: currentPage,
          limit: validPageSize,
          search_text: searchText || text,
        });
        const totalRecords=response?.data?.count ?? 1
        const rawData = response?.data.response || [];
        setTotalPages(Math.ceil(totalRecords/ pageSize));
        setTotalCount(totalRecords);
        setViewNumberData(rawData);
      } catch (error) {
        console.error("Error fetching View Number usage:", error);
        setApiError(error);
      }
    },
    [currentPage, pageSize, searchText]
  );

  const handleRowSelect = useCallback(
    (row) => {
      const findData = selectedRowsData.find((item) => item._id === row._id);
      setSelectedRowsData((prev) => {
        if (findData) {
          return prev.filter((item) => item._id !== findData._id);
        } else {
          return [...prev, row];
        }
      });
    },
    [setSelectedRowsData, selectedRowsData]
  );

  const handleUserAction = async ({ updatedPropertyType, id: selectedId }) => {
    try {
      history.push(`${updatedPropertyType}?id=${selectedId}`);
    } catch (error) {
      console.error("Error handling user action:", error);
      const errorMessage =
        error?.response?.viewNumberData?.error?.message ||
        error?.response?.viewNumberData?.error;
      showToast({
        message: `Error: ${errorMessage}`,
        success: false,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });
    }
  };
  const handleInputFormSubmit = (e) => {
    e.preventDefault();
    fetchData(searchText);
  };


  useEffect(() => {
    if (queryObject?.search_text) {
      setSearchText(queryObject?.search_text);
      handleFetchSearch(queryObject?.search_text);
    } else {
      setSearchText("");
      fetchData();
    }
  }, [currentPage, pageSize, queryObject?.search_text]);

  const columns = useMemo(() => viewNumberTableColumn, [viewNumberTableColumn]);

  const selectedRowsIds = selectedRowsData.map((item) => item._id);
  const selectedData = useMemo(
    () =>
      selectedRowsIds.map((id) => viewNumberData.find((row) => row._id === id)),
    [selectedRowsIds, viewNumberData]
  );

  const lsLocalStorageManageColumns = getFilterFromLocalStorage(
    ENTITY_COLUMNS_TYPES.VIEW_NUMBER_COLUMNS
  );

  const [columnVisibility, setColumnVisibility] = useState(
    lsLocalStorageManageColumns
      ? lsLocalStorageManageColumns
      : columns.reduce((acc, column) => {
          acc[column.accessor] = true;
          return acc;
        }, {})
  );

  const handleFetchSearch = useCallback(
    (text) => {
      setCurrentPage(1);
      fetchData(text);
    },
    [fetchData]
  );

  useEffect(() => {
    setFilterToLocalStorage(
      ENTITY_COLUMNS_TYPES.VIEW_NUMBER_COLUMNS,
      columnVisibility
    );
  }, [columnVisibility]);

  const visibleColumns = columns.filter(
    (column) => columnVisibility[column.accessor]
  );

  if (apiError) {
    return <ErrorDisplay error={apiError} />;
  }

  return (
    <>
    <Box height="100vh" bg="#F8FAFB">
      <Flex
        justify="space-between"
        align="center"
        p="2"
        boxShadow="sm"
        borderRadius="sm"
        pos="fixed"
        style={{ width: "calc(100vw - 96px)" }}
        zIndex={1000}
      >
        <form onSubmit={handleInputFormSubmit}>
              <InputGroup  size="sm">
                <SearchBar
                  background="#F9F9FB"
                  color="#637381"
                  setSearchText={(e) => setSearchText(e.target.value)}
                  searchText={searchText}
                  borderRadius="5px"
                  borderWidth="0.5px"
                  borderColor="#637381"
                />
              </InputGroup>
            </form>
        <Text
          borderRadius="5px"
          borderWidth="0.5px"
          borderColor="#637381"
          background="#F9F9FB"
          color="#637381"
          p={1}
        >
          Total Records: {totalCount}
        </Text>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(value) => setCurrentPage(value)}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setPageSizeToLocalStorage={(value) =>
            setFilterToLocalStorage(
              ENTITY_FILTER_TYPES.USERS_PAGE_SIZE,
              value
            )
          }
        />
      </Flex>
      <SidebarFilter
       
      />
      {viewNumberData?.length !== 0 ? (
        <>
          <CustomTable
            columns={visibleColumns}
            data={viewNumberData}
            handleRowSelect={handleRowSelect}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
            onSelectedAction={setSelectedAction}
            menuComponent={
              <Menu
                data={Object.values(USER_STATUS)}
                onUpdate={() => {}}
                onUpdatePropertyStatus={handleUserAction}
                selectedAction={selectedAction}
              />
            }
          />
        </>
      ) : !loading ? (
        <NoDataFound />
      ) : null}
      <OverlayLoaderComponent isOpen={loading} />
    </Box>
  </>
  );
}
